// OOO-DELEGATE in the in-memory API: a person's out-of-office windows (services/api/src/routes/
// ooo.ts). Empty until set; a window belongs to the caller's membership in the company.
import type { OperationOutput, RouteId } from "@companyos/sdk";
import { type Handler, json, notFound, problem } from "./fake";

type Win = OperationOutput<"ooo.list">["data"][number];

export const FAKE_OOO_ROUTES = [
  "ooo.list",
  "ooo.set",
  "ooo.end",
  "ooo.returnSummary",
  "ooo.dismissSummary",
] as const satisfies readonly RouteId[];

export function createOooFake(deps: { now: () => Date }) {
  const windows: (Win & { company_id: string })[] = [];
  const strip = ({ company_id: _c, ...w }: Win & { company_id: string }): Win => w;
  const handlers: Partial<Record<RouteId, Handler>> = {
    "ooo.list": ({ principal }) => {
      if (!principal) return notFound();
      const me = principal.me.memberships.find((m) => m.company_id === principal.company.id);
      const now = deps.now().toISOString();
      return json({
        data: windows
          .filter(
            (w) =>
              w.company_id === principal.company.id &&
              w.membership_id === me?.membership_id &&
              !w.ended_at &&
              w.ends_at > now,
          )
          .map(strip),
      });
    },
    "ooo.set": ({ principal, body }) => {
      if (!principal) return notFound();
      const me = principal.me.memberships.find((m) => m.company_id === principal.company.id);
      if (!me) return problem(403, "forbidden", "Forbidden");
      const b = (body ?? {}) as Record<string, unknown>;
      const starts = typeof b.starts_at === "string" ? b.starts_at : "";
      const ends = typeof b.ends_at === "string" ? b.ends_at : "";
      if (!starts || !ends || ends <= starts)
        return problem(422, "validation_failed", "Validation failed", "ends_at: after the start", {
          errors: [{ path: "ends_at", code: "invalid", message: "must be after the start" }],
        });
      const w = {
        company_id: principal.company.id,
        id: crypto.randomUUID(),
        membership_id: me.membership_id,
        seat_id: typeof b.seat_id === "string" ? b.seat_id : null,
        delegate_membership_id:
          typeof b.delegate_membership_id === "string" ? b.delegate_membership_id : null,
        starts_at: starts,
        ends_at: ends,
        ended_at: null,
      };
      windows.push(w);
      return json(strip(w), 201);
    },
    "ooo.end": ({ principal, params }) => {
      if (!principal) return notFound();
      const w = windows.find((x) => x.company_id === principal.company.id && x.id === params.id);
      if (!w) return notFound();
      w.ended_at ??= deps.now().toISOString();
      return json(strip(w));
    },
    "ooo.returnSummary": ({ principal }) => (principal ? json({ data: [] }) : notFound()),
    "ooo.dismissSummary": ({ principal, params }) => {
      if (!principal) return notFound();
      const w = windows.find((x) => x.company_id === principal.company.id && x.id === params.id);
      return w ? json(strip(w)) : notFound();
    },
  };
  return { handlers };
}
